import React, { memo, useState, useCallback } from 'react';
import cx from 'classnames';
import { Typography } from '@mui/material';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { NodeBox } from '../../components/NodeBox';
import { ResourceName } from '../../components/ResourceName';
import { Stack } from 'new/elements/Stack';
import styles from './ApiServer.module.scss';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import PipelineStatusBadge, {
  Status,
} from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import { Button } from 'new/elements/Button';
import { useCreateApiRequest } from 'new/hooks/useCreateApiRequest';
import { useApiRequestModal } from 'new/components/flows/types/api_server/context/ApiRequestModal/ApiRequestModalContext';
import { CREATE_API_REQUEST_REQUEST } from 'js/constants/actionTypes';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { ConnectionLoader } from 'new/elements/ConnectionLoader/ConnectionLoader';

interface ApiServerProps {
  data: any;
  isPreview?: boolean;
}

const METHOD_COLORS = {
  GET: 'var(--color-success-700)',
  POST: 'var(--color-primary-700)',
  PUT: 'var(--color-warning-700)',
  DELETE: 'var(--color-danger-700)',
};

export const ApiServer = memo((props: ApiServerProps) => {
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [isCreatingNode, setIsCreatingNode] = useState<boolean>(false);
  const { data, isPreview } = props;
  const { createApiRequestNode } = useCreateApiRequest();
  const { openConfigModal } = useApiRequestModal();
  const { getNodes, getEdges } = useReactFlow();
  const isRequestLoading = useIsLoading(CREATE_API_REQUEST_REQUEST);
  const isLoading = isRequestLoading || isCreatingNode;

  // check which methods have connected request nodes
  const getConnectedMethods = useCallback(() => {
    const edges = getEdges();
    const connectedMethods = new Set<string>();

    edges.forEach((edge) => {
      if (edge.source === `node-${data.flow_node_id}` && edge.sourceHandle) {
        const method = edge.sourceHandle.split('-')[0].toUpperCase();
        if (METHOD_COLORS[method]) {
          connectedMethods.add(method);
        }
      }
    });

    return connectedMethods;
  }, [data.flow_node_id, getNodes, getEdges]);

  const connectedMethods = getConnectedMethods();

  const waitForNode = (datasetId: number): Promise<void> => {
    return new Promise((resolve) => {
      const checkForNode = () => {
        const allNodes = getNodes();
        if (allNodes.some((n: any) => n.data?.item?.id === datasetId)) {
          resolve();
        } else {
          setTimeout(checkForNode, 100);
        }
      };

      checkForNode();
    });
  };

  const handleMethodClick = async (method: string) => {
    setSelectedMethod(method);
    setIsCreatingNode(true);

    try {
      const newNodeId = await createApiRequestNode(data, method);
      await waitForNode(newNodeId);
      openConfigModal(Number(newNodeId), data?.id, method);
    } finally {
      setIsCreatingNode(false);
    }
  };

  return (
    <div className={styles.apiServerWrapper}>
      <NodeBox data={data} className={styles.container}>
        <ResourceName {...props} selectActions={['activate', 'delete']}>
          API Server
        </ResourceName>
        <div className={styles.apiServerContainer}>
          <NodeBox.Title>{data?.name}</NodeBox.Title>
        </div>
        <NodeBox.Footer className={styles.apiServerFooter}>
          <Stack direction="column" spacing={1}>
            <PipelineStatusBadge
              id={data.id}
              status={data.status as Status}
              className={styles.lightningStatus}
              showRuntimeIndicator={false}
              parentArchivalStatus={data?.archival_status}
            />
            <ChipWithCopy tooltipTitle="Copy API Key ID" toCopy={data.id}>
              ID {data.id}
            </ChipWithCopy>
          </Stack>
        </NodeBox.Footer>
      </NodeBox>
      <div className={styles.methodTabs}>
        {Object.entries(METHOD_COLORS).map(([method, color]) => {
          const isConnected = connectedMethods.has(method);
          return (
            <div key={method} className={styles.methodTabWrapper}>
              <div
                className={cx(
                  styles.methodIndicator,
                  isConnected
                    ? styles.methodIndicatorConnected
                    : styles.methodIndicatorDisconnected,
                )}
                style={{
                  backgroundColor: isConnected ? color : 'transparent',
                  borderColor: color,
                }}
              />
              <div className={styles.methodTabContainer}>
                {connectedMethods.size === 0 && (
                  <div
                    className={cx(styles.methodHandlePulse, styles[`${method.toLowerCase()}Pulse`])}
                  />
                )}
                <Button
                  onClick={() => handleMethodClick(method)}
                  className={cx(
                    styles.methodTab,
                    selectedMethod === method && styles.selectedMethodTab,
                  )}
                  disabled={isPreview}
                  data-analytics={`api_server_method_${method.toLowerCase()}`}
                >
                  <Typography variant="tiny">{method}</Typography>
                </Button>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={`${method.toLowerCase()}-handle`}
                  title={`Drag to create ${method} request`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 0,
                    boxShadow: 'none',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <ConnectionLoader loading={isLoading} message="Creating API request..." />
    </div>
  );
});
